/**
 * Migration API functions
 *
 * Calls the isolation migration router endpoints.
 */

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

export interface MigrationValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
  estimated_data_size_mb: number;
  estimated_row_count: number;
}

export interface MigrationStartResult {
  status: string;
  tenant_id: string;
  tenant_slug: string;
  direction: string;
  message: string;
}

export interface MigrationProgress {
  tenant_id: string;
  tenant_slug: string;
  direction: string;
  status: string; // "pending", "in_progress", "completed", "failed"
  current_step: number;
  total_steps: number;
  progress_pct: number;
  message: string;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  rows_migrated: number;
  tables_migrated: number;
}

export interface MigrationStatus {
  tenant_id: string;
  tenant_slug: string;
  current_isolation: string;
  has_active_migration: boolean;
  progress: MigrationProgress | null;
}

export async function validateMigration(
  authFetch: AuthFetch,
  tenantId: string,
  targetIsolation: string,
): Promise<MigrationValidation> {
  const res = await authFetch(`/api/admin/migrations/${tenantId}/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ target_isolation: targetIsolation }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'Validation failed');
  }
  return res.json();
}

export async function startMigration(
  authFetch: AuthFetch,
  tenantId: string,
  targetIsolation: string,
): Promise<MigrationStartResult> {
  const res = await authFetch(`/api/admin/migrations/${tenantId}/migrate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ target_isolation: targetIsolation }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail?.message || err.detail || 'Migration start failed');
  }
  return res.json();
}

export async function fetchMigrationProgress(
  authFetch: AuthFetch,
  tenantId: string,
): Promise<MigrationProgress> {
  const res = await authFetch(`/api/admin/migrations/${tenantId}/progress`);
  if (!res.ok) throw new Error('Failed to fetch progress');
  return res.json();
}

export async function fetchMigrationStatus(
  authFetch: AuthFetch,
  tenantId: string,
): Promise<MigrationStatus> {
  const res = await authFetch(`/api/admin/migrations/${tenantId}/status`);
  if (!res.ok) throw new Error('Failed to fetch status');
  return res.json();
}
